import { AsyncUnzipInflate, AsyncZipDeflate, Unzip, Zip } from 'fflate';
import type { LimitedNodeApi } from '@mimir/common/nodejs/limitedNodeApi/nodeApi';
import type { fs as LimitedFsNs } from '@mimir/common/nodejs/limitedNodeApi/fs';
import { makeDirPAsync, read, close, open, write } from '@mimir/common/util/fs';

import type {
  CreateZipFromDirectoryRequest,
  ExtractZipToDirectoryRequest,
  MzipOps,
} from './mzipOps';

interface FileEntry {
  fullPath: string;
  relativePath: string;
  isDirectory: boolean;
}

export function makeDefaultMzipOps(): MzipOps {
  return {
    createZipFromDirectory,
    extractZipToDirectory,
  };
}

function readdirAsync(nodeApi: LimitedNodeApi, dirPath: string): Promise<LimitedFsNs.Dirent[]> {
  return new Promise((resolve, reject) => {
    nodeApi.fs.readdir(dirPath, { withFileTypes: true }, (err, files) => {
      if (err !== null) {
        reject(err);
        return;
      }
      resolve(files);
    });
  });
}

const IO_CHUNK_SIZE = 256 * 1024;


async function writeFullChunk(
  nodeApi: LimitedNodeApi,
  fd: number,
  data: Uint8Array,
  position: number
): Promise<void> {
  await write(nodeApi, fd, data, 0, data.byteLength, position);
}

function toStableUint8Array(data: Uint8Array): Uint8Array {
  return new Uint8Array(data);
}

function toZipPath(relativePath: string): string {
  return relativePath.split('\\').join('/');
}

function yieldToEventLoopAsync(): Promise<void> {
  return new Promise((resolveYield) => {
    setTimeout(resolveYield, 0);
  });
}

async function pushFileToZipAsync(
  nodeApi: LimitedNodeApi,
  filePath: string,
  zipFile: AsyncZipDeflate
): Promise<void> {
  const fd = await open(nodeApi, filePath, 'r');
  let position = 0;
  const chunkBuffer = new Uint8Array(IO_CHUNK_SIZE);

  try {
    while (true) {
      const { bytesRead, buffer: returnedBuffer } = await read(nodeApi, fd, chunkBuffer, 0, chunkBuffer.byteLength, position);

      if (bytesRead === 0) {
        break;
      }

      const chunk = toStableUint8Array(returnedBuffer.subarray(0, bytesRead));
      zipFile.push(chunk, false);
      position += bytesRead;

      await yieldToEventLoopAsync();
    }

    zipFile.push(new Uint8Array(0), true);
  } finally {
    await close(nodeApi, fd);
  }
}

async function collectFilesRecursive(
  nodeApi: LimitedNodeApi,
  sourceDirectoryPath: string
): Promise<FileEntry[]> {
  const out: FileEntry[] = [];
  const queue: Array<{ fullPath: string; relativePath: string }> = [
    { fullPath: sourceDirectoryPath, relativePath: '' },
  ];

  while (queue.length > 0) {
    const current = queue.shift();
    if (current == null) {
      continue;
    }

    const children = await readdirAsync(nodeApi, current.fullPath);

    for (const child of children) {
      const childFullPath = nodeApi.path.join(current.fullPath, child.name);
      const childRelativePath =
        current.relativePath === '' ? child.name : `${current.relativePath}/${child.name}`;

      if (child.isDirectory()) {
        queue.push({ fullPath: childFullPath, relativePath: childRelativePath });
        out.push({ fullPath: childFullPath, relativePath: childRelativePath, isDirectory: true });
      } else if (child.isFile()) {
        out.push({ fullPath: childFullPath, relativePath: childRelativePath, isDirectory: false });
      }
    }
  }

  return out;
}

async function createZipFromDirectory(request: CreateZipFromDirectoryRequest): Promise<void> {
  const { nodeApi, sourceDirectoryPath, outputZipFilePath } = request;

  const fileEntries = await collectFilesRecursive(nodeApi, sourceDirectoryPath);

  await makeDirPAsync(nodeApi, nodeApi.path.dirname(outputZipFilePath));
  const fd = await open(nodeApi, outputZipFilePath, 'w');

  let writeOffset = 0;
  let writeQueue: Promise<void> = Promise.resolve();

  try {
    await new Promise<void>((resolve, reject) => {
      const zipStream = new Zip((err, chunk, final) => {
        if (err != null) {
          reject(err);
          return;
        }

        const chunkCopy = toStableUint8Array(chunk);
        const chunkOffset = writeOffset;
        writeOffset += chunkCopy.byteLength;

        writeQueue = writeQueue.then(() => writeFullChunk(nodeApi, fd, chunkCopy, chunkOffset));

        if (final) {
          void writeQueue.then(resolve, reject);
        }
      });

      void (async () => {
        try {
          for (const fileEntry of fileEntries) {
            const zipPath = toZipPath(fileEntry.relativePath);

            if (fileEntry.isDirectory) {
              const zipDir = new AsyncZipDeflate(zipPath + '/');
              zipStream.add(zipDir);
              zipDir.push(new Uint8Array(0), true);
              continue;
            }

            const zipFile = new AsyncZipDeflate(zipPath, { level: 6 });
            zipStream.add(zipFile);
            await pushFileToZipAsync(nodeApi, fileEntry.fullPath, zipFile);
            await yieldToEventLoopAsync();
          }

          zipStream.end();
        } catch (ex: unknown) {
          reject(ex);
        }
      })();
    });
  } finally {
    await close(nodeApi, fd);
  }
}

function isValidZipHeader(buffer: Uint8Array): boolean {
  // A valid ZIP starts with PK\x03\x04 (local file header) or PK\x05\x06 (empty archive).
  const isLocalFileHeader = buffer[0] === 0x50 && buffer[1] === 0x4b && buffer[2] === 0x03 && buffer[3] === 0x04;
  const isEmptyArchive    = buffer[0] === 0x50 && buffer[1] === 0x4b && buffer[2] === 0x05 && buffer[3] === 0x06;
  return isLocalFileHeader || isEmptyArchive;
}

async function runExtractionLoop(nodeApi: LimitedNodeApi, sourceFd: number, unzipStream: Unzip): Promise<void> {
  const readBuffer = new Uint8Array(IO_CHUNK_SIZE);
  let position = 0;
  let firstChunk = true;
  while (true) {
    const { bytesRead, buffer: returnedBuffer }  = await read(
      nodeApi,
      sourceFd,
      readBuffer,
      0,
      readBuffer.byteLength,
      position
    );

    if (bytesRead === 0) {
      break;
    }

    if (firstChunk) {
      firstChunk = false;
      if (!isValidZipHeader(returnedBuffer)) {
        throw new Error('Invalid ZIP file: missing PK magic bytes');
      }
    }

    unzipStream.push(toStableUint8Array(returnedBuffer.subarray(0, bytesRead)), false);
    position += bytesRead;

    await yieldToEventLoopAsync();
  }
}
async function extractZipToDirectory(request: ExtractZipToDirectoryRequest): Promise<void> {
  const { nodeApi, sourceZipFilePath, outputDirectoryPath } = request;

  await makeDirPAsync(nodeApi, outputDirectoryPath);

  await new Promise<void>((resolve, reject) => {
    let totalEntries = 0;
    let completedEntries = 0;
    let pushedFinalChunk = false;
    let settled = false;

    const maybeResolve = () => {
      if (!settled && pushedFinalChunk && completedEntries === totalEntries) {
        settled = true;
        resolve();
      }
    };

    const fail = (ex: unknown) => {
      if (!settled) {
        settled = true;
        reject(ex);
      }
    };

    const unzipStream = new Unzip((file) => {
      totalEntries += 1;
      const outputPath = nodeApi.path.join(outputDirectoryPath, file.name);

      if (file.name.endsWith('/')) {
        void makeDirPAsync(nodeApi, outputPath)
          .then(() => {
            completedEntries += 1;
            maybeResolve();
          })
          .catch(fail);
        return;
      }

      let fd: number | null = null;
      let fileOffset = 0;
      let fileWriteQueue: Promise<void> = Promise.resolve();

      file.ondata = (err, chunk, final) => {
        if (err != null) {
          fail(err);
          return;
        }

        const chunkCopy = toStableUint8Array(chunk);

        fileWriteQueue = fileWriteQueue.then(async () => {
          if (fd === null) {
            await makeDirPAsync(nodeApi, nodeApi.path.dirname(outputPath));
            fd = await open(nodeApi, outputPath, 'w');
          }

          await write(nodeApi, fd, chunkCopy, 0, chunkCopy.byteLength, fileOffset);
          fileOffset += chunkCopy.byteLength;
        });

        if (final) {
          void fileWriteQueue
            .then(async () => {
              if (fd !== null) {
                await close(nodeApi, fd);
              }
              completedEntries += 1;
              maybeResolve();
            })
            .catch(fail);
        }
      };

      file.start();
    });

    unzipStream.register(AsyncUnzipInflate);

    void (async () => {
      const sourceFd = await open(nodeApi, sourceZipFilePath, 'r');
      try {
        await runExtractionLoop(nodeApi, sourceFd, unzipStream);

        unzipStream.push(new Uint8Array(0), true);
        pushedFinalChunk = true;
        maybeResolve();
      } catch (ex: unknown) {
        fail(ex);
      } finally {
        try {
          await close(nodeApi, sourceFd);
        } catch (ex: unknown) {
          fail(ex);
        }
      }
    })();
  });
}
